(() => {
  if (customElements.get('dope-quiz')) return;
  const LABELS = {
    who: { me: 'myself', gift: 'a gift', team: 'a team or event' },
    category: { paddles: 'a paddle', balls: 'pickleballs', apparel: 'apparel', accessories: 'accessories', gifts: 'gift ideas' },
    style: { 'high-roller': 'black and gold', 'manhattan-mint': 'cool mint', custom: 'custom colors or artwork', any: 'open to any style' },
    budget: { '25': 'under $25 USD', '50': 'under $50 USD', '100': 'under $100 USD', any: 'no fixed budget' },
    use: { casual: 'casual play', sanctioned: 'sanctioned competition', everyday: 'everyday use or display' },
  };
  class DopeQuiz extends HTMLElement {
    static destination(value, origin) {
      if (typeof value !== 'string' || !value.trim()) return null;
      try {
        const url = new URL(value, origin);
        return url.origin === origin && /^https?:$/.test(url.protocol) ? url.href : null;
      } catch { return null; }
    }
    static match(answers, data, origin) {
      const destinations = data.destinations || {};
      const valid = (key) => {
        const item = destinations[key];
        return item && this.destination(item.url, origin) ? { key, ...item } : null;
      };
      if (answers.who === 'team' || answers.style === 'custom') return [valid('custom')].filter(Boolean);
      const cap = answers.budget === 'any' ? Infinity : Number(answers.budget) * 100;
      const products = (Array.isArray(data.products) ? data.products : [])
        .filter((product) => product && this.destination(product.url, origin))
        .filter((product) => answers.category === 'gifts' || product.category === answers.category)
        .filter((product) => !(answers.use === 'sanctioned' && ['paddles', 'balls'].includes(product.category) && product.sanctioned !== true))
        .map((product) => {
          const options = (Array.isArray(product.variants) ? product.variants : [])
            .filter((variant) => variant.available === true && Number.isFinite(variant.price) && variant.price < cap)
            .sort((a, b) => a.price - b.price);
          if (!options.length) return null;
          const option = options[0];
          const url = new URL(product.url, origin);
          if (/^\d+$/.test(String(option.id))) url.searchParams.set('variant', option.id);
          if (/^[A-Z]{3}$/.test(data.currency || '')) url.searchParams.set('currency', data.currency);
          if (/^[A-Z]{2}$/.test(data.country || '')) url.searchParams.set('country', data.country);
          const selection = option.title && option.title !== 'Default Title' ? `${option.title} · ${option.label}` : `Options from ${option.label}`;
          return { ...product, url: url.href, key: product.handle, text: `${selection}. Check your selected size or pack and current availability.` };
        }).filter(Boolean)
        .sort((a, b) => Number(b.style === answers.style) - Number(a.style === answers.style)).slice(0, 3);
      if (!products.length && answers.budget === 'any') {
        const fallback = valid(answers.category);
        if (fallback) products.push({ ...fallback, text: 'Browse current options. This collection may include other categories; check the selected option’s price and availability.' });
      }
      if (answers.style === 'manhattan-mint') {
        const mint = valid('manhattan-mint');
        if (mint) products.push(mint);
      }
      return products.slice(0, 4);
    }
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.form = this.querySelector('[data-quiz-form]');
      if (!this.form) return;
      this.steps = [...this.form.querySelectorAll('[data-step]')];
      this.dots = [...this.querySelectorAll('[data-step-dot]')];
      this.back = this.querySelector('[data-back]');
      this.continueButton = this.querySelector('[data-continue]');
      this.hint = this.querySelector('[data-hint]');
      this.progress = this.querySelector('[data-progress-text]');
      this.results = this.querySelector('[data-results]');
      this.cards = this.querySelector('[data-cards]');
      this.template = this.querySelector('[data-card-template]');
      this.prompt = this.querySelector('[data-prompt]');
      this.copyStatus = this.querySelector('[data-copy-status]');
      try { this.data = JSON.parse(this.querySelector('[data-quiz-results]')?.textContent || '{}'); }
      catch { this.data = {}; }
      const options = { signal: this.abort.signal };
      this.form.addEventListener('change', () => this.updateControls(), options);
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.next(); }, options);
      this.back?.addEventListener('click', () => this.show(this.index - 1), options);
      this.querySelector('[data-restart]')?.addEventListener('click', () => this.reset(), options);
      this.querySelector('[data-copy]')?.addEventListener('click', () => this.copy(), options);
      this.querySelector('[data-edit]')?.addEventListener('click', () => this.show(0), options);
      this.index = Math.min(this.index || 0, this.steps.length - 1);
      this.setAttribute('data-ready', '');
      if (this.finished) this.finish(false);
      else this.show(this.index, false);
    }
    disconnectedCallback() { this.abort?.abort(); }
    updateControls() {
      const selected = !!this.steps[this.index]?.querySelector('input:checked');
      this.continueButton.disabled = !selected;
      this.continueButton.textContent = this.index === this.steps.length - 1 ? 'See my matches' : 'Continue';
      this.hint.textContent = selected ? 'You can change this answer before continuing.' : 'Choose one answer to continue.';
    }
    show(index, focus = true) {
      this.finished = false;
      this.index = Math.max(0, Math.min(index, this.steps.length - 1));
      this.steps.forEach((step, i) => { step.hidden = i !== this.index; });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'step');
        else dot.removeAttribute('aria-current');
        dot.classList.toggle('is-done', i < this.index);
      });
      this.back.hidden = this.index === 0;
      this.progress.textContent = `Question ${this.index + 1} of ${this.steps.length}`;
      this.results.hidden = true;
      this.form.hidden = false;
      this.updateControls();
      if (focus) this.steps[this.index].querySelector('legend')?.focus({ preventScroll: true });
    }
    next() {
      if (!this.steps[this.index]?.querySelector('input:checked')) return;
      if (this.index < this.steps.length - 1) this.show(this.index + 1);
      else this.finish();
    }
    answers() {
      return Object.fromEntries(this.steps.map((step) => [step.dataset.step, step.querySelector('input:checked')?.value]).filter(([, value]) => value));
    }
    finish(focus = true) {
      const answers = this.answers();
      if (this.steps.some((step) => !answers[step.dataset.step])) return;
      const matches = DopeQuiz.match(answers, this.data, location.origin);
      this.cards.replaceChildren(...matches.map((result) => this.card(result)));
      const constrained = answers.use === 'sanctioned' && ['paddles', 'balls', 'gifts'].includes(answers.category);
      this.querySelector('[data-result-note]').textContent = constrained
        ? 'Sanctioned play is a buying constraint. High Roller’s metallic gold finish paddle is not USA Pickleball approved. We have not verified a competition-approved match here; check the exact model against your event’s current rules before purchasing.'
        : matches.length ? 'These matches use the current catalog and available options when this page loaded. Open each product to confirm your selection.' : 'We could not find an available match within those choices. Change your answers or ask the Dope team for help.';
      this.buildPrompt(answers);
      this.finished = true;
      this.form.hidden = true;
      this.results.hidden = false;
      this.progress.textContent = 'Your matches are ready';
      this.dots.forEach((dot) => { dot.classList.add('is-done'); dot.removeAttribute('aria-current'); });
      if (focus) this.results.querySelector('[data-results-heading]')?.focus({ preventScroll: true });
    }
    card(result) {
      const node = this.template.content.firstElementChild.cloneNode(true);
      node.querySelector('a').href = DopeQuiz.destination(result.url, location.origin);
      node.querySelector('.dope-quiz__card-title').textContent = result.heading;
      node.querySelector('.dope-quiz__card-text').textContent = result.text || '';
      if (result.image) {
        try {
          const url = new URL(result.image, location.origin);
          if (url.protocol === 'https:' && (url.origin === location.origin || url.hostname === 'cdn.shopify.com')) {
            const img = document.createElement('img');
            img.src = url.href; img.alt = ''; img.loading = 'lazy'; img.width = 600; img.height = 600;
            node.querySelector('.dope-quiz__card-media').append(img);
          }
        } catch { /* The text link remains useful if an image is unavailable. */ }
      }
      return node;
    }
    buildPrompt(answers) {
      const details = Object.entries(LABELS).map(([key, labels]) => `${key === 'who' ? 'Recipient' : key[0].toUpperCase() + key.slice(1)}: ${(key === 'budget' ? this.data.budgetLabels?.[answers[key]] : null) || labels[answers[key]] || 'not specified'}.`).join('\n');
      this.prompt.value = `Help me find my Dope.\n${details}\nUse current products at ${this.dataset.shopUrl || location.origin}. Check the exact selected option’s price, contents and availability. Verify sanctioned-play eligibility if relevant. Manhattan Mint is in development. For custom or team orders, help me prepare a quote request. Please flag anything you cannot verify and let me speak to the Dope team when needed.`;
      this.copyStatus.textContent = '';
    }
    async copy() {
      const signal = this.abort.signal;
      try {
        await navigator.clipboard.writeText(this.prompt.value);
        if (!signal.aborted) this.copyStatus.textContent = 'Copied. Open the chat bubble and paste your brief to start a conversation.';
      } catch {
        if (signal.aborted) return;
        this.prompt.focus(); this.prompt.select();
        this.copyStatus.textContent = 'Copy is unavailable in this browser. Your brief is selected; copy it manually and paste it into chat.';
      }
    }
    reset() {
      this.form.reset();
      this.cards.replaceChildren();
      this.prompt.value = '';
      this.copyStatus.textContent = '';
      this.show(0);
    }
  }
  customElements.define('dope-quiz', DopeQuiz);
})();
